import { useState, useEffect, useContext } from 'react';
import { NavLink } from 'react-router-dom';
import AuthContext from '../../contexts/auth-context';
import AppContext from '../../contexts/app-context';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import useAxios from '../../hooks/useAxios';
import { urls, PAGE_SIZE, app_paths, patientFormType } from '../config/variables';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CircularProgress from '@mui/material/CircularProgress';
import classes from './PatientListSection.module'

const PatientCard = ({ patient }) => {
  const appCtx = useContext(AppContext);
  const userRole = appCtx.userRole;

  const handleSelectUser = () => {
    appCtx.addSelectedUser(patient);
  };

  var navigationpath = app_paths.PATIENT_PROFILE.replace(':patientID', patient.id);
  if (['doctor', 'pharmacy_admin'].includes(userRole)) {
    navigationpath = app_paths.NEW_ENCOUNTER.replace(':patientID', patient.id);
  }

  return (
    <NavLink onClick={handleSelectUser} to={`${navigationpath}`} style={classes.link}>
      <Box sx={classes.patient_card}>
        <Avatar
          alt={patient.user.name}
          src={patient.user.gender === 'F' ? '/images/patient_woman.svg' : '/images/patient_man.svg'}
        />
        <Typography sx={classes.patient_card_username}>
          {patient.user.name}
        </Typography>
      </Box>
    </NavLink>
  );
};

const PatientListSection = () => {
  const authCtx = useContext(AuthContext);
  const appCtx = useContext(AppContext);
  const { operation } = useAxios();

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearch, setIsSearch] = useState(false);
  const [usersList, setUsersList] = useState();
  const [loadingUsersList, setLoadingUsersList] = useState(false);
  const [usersListError, setUsersListError] = useState();
  const [count, setCount] = useState(1);
  const [page, setPage] = useState(1);

  const reformateData = (sliceIdx) => {
    return sliceIdx.map((patient) => {
      return <PatientCard key={patient.id} patient={patient} />;
    });
  };

  const setFilteredUserList = (users) => {
    setLoadingUsersList(false);
    setUsersList(reformateData(users.results));
    setCount(Math.ceil(users.count / PAGE_SIZE));
    appCtx.addUsersList(users);
  };

  const getUsersList = (filter) => {
    setLoadingUsersList(true);
    operation(
      {
        method: 'GET',
        url: urls.LIST_PATIENTS + filter,
        headers: { Authorization: `token ${authCtx.token}` },
        data: {},
      },
      setFilteredUserList,
      handleUsersListError
    );
  };

  const handleUsersListError = (error) => {
    setLoadingUsersList(false);
    setUsersListError(error);
  };

  useEffect(() => {
    if (!appCtx.reFetchUsersList) {
      const searchForPatient = `?searchfor=${searchQuery}`;
      const filter = searchQuery
        ? `${searchForPatient}&page=${page}&page_size=${PAGE_SIZE}`
        : `?page=${page}&page_size=${PAGE_SIZE}`;
      getUsersList(filter);
    }
    // eslint-disable-next-line
  }, [page]);

  const handleSearchClick = (event) => {
    if (searchQuery !== '') {
      event.preventDefault();
      setIsSearch(true);
      setPage(1);
      getUsersList(`?searchfor=${searchQuery}&page=1&page_size=${PAGE_SIZE}`);
    }
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClearSearch = () => {
    setIsSearch(false);
    setSearchQuery('');
    setPage(1);
    getUsersList(`?page=1&page_size=${PAGE_SIZE}`);
  };

  useEffect(() => {
    if (isSearch && searchQuery === '') {
      handleClearSearch();
    }
    // eslint-disable-next-line
  }, [searchQuery, isSearch]);

  useEffect(() => {
    if (appCtx.reFetchUsersList) {
      if (appCtx.reFetchUsersList === patientFormType.create) {
        getUsersList(`?page=1&page_size=${PAGE_SIZE}`);
        setPage(1);
      } else {
        getUsersList(`?page=${page}&page_size=${PAGE_SIZE}`);
      }
    }
    // eslint-disable-next-line
  }, [appCtx.reFetchUsersList]);

  return (
    <Box sx={classes.container}>
      <TextField
        fullWidth
        variant="outlined"
        InputProps={{
          startAdornment: searchQuery && (
            <InputAdornment position="start">
              <IconButton onClick={handleClearSearch}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleSearchClick}
                sx={classes.search_button}
                disabled={!searchQuery}
              >
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        value={searchQuery}
        onChange={handleSearch}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleSearchClick(event);
          }
        }}
        placeholder="Search patients..."
        sx={classes.search}
        size="small"
      />

      {usersListError ? (
        <Alert severity="error">
          <AlertTitle>User List Not Found</AlertTitle>
        </Alert>
      ) : (
        <>
          {loadingUsersList ? (
            <Box sx={classes.usersListCircularProgress}>
              <CircularProgress />
            </Box>
          ) : isSearch && usersList?.length === 0 ? (
            <Alert severity="warning">
              <AlertTitle>Patient Not Found</AlertTitle>
            </Alert>
          ) : (
            <>
              {count > 0 && (
                <Box sx={classes.patients_container}>
                  {usersList}
                  <Box sx={classes.pagination}>
                    <Stack spacing={2} sx={classes.stack}>
                      <Pagination
                        page={page}
                        disabled={count === 1}
                        count={count}
                        variant="outlined"
                        color="primary"
                        size="small"
                        onChange={(e, v) => setPage(v)}
                      />
                    </Stack>
                  </Box>
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default PatientListSection;
